"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import studentImage from "../resources/4884388.jpg"
import teacherImage from "../resources/4905784.jpg"
import { ArrowRight, ArrowLeft, Loader, X, Clock, Eye, EyeOff, CheckCircle, Menu } from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { postAws } from "../aws"
import { getFCMToken, saveTokenToServer } from "../firebase-messaging"

export default function LoginPage() {
  const [isStudent, setIsStudent] = useState(true)
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  })
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const navigate = useNavigate()
  const [showOtpModal, setShowOtpModal] = useState(false)
  const [otp, setOtp] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmNewPassword, setConfirmNewPassword] = useState("")
  const [timeLeft, setTimeLeft] = useState(300)
  const [isTimedOut, setIsTimedOut] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [otpError, setOtpError] = useState("")
  const [passwordError, setPasswordError] = useState("")
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    const token = localStorage.getItem("token")
    const studentId = localStorage.getItem("studentId")
    if (token && studentId) {
      navigate("/dashboard")
    }
  }, [navigate])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError("") // Clear previous errors

    try {
      const loginData = {
        body: {
          studentEmail: formData.email,
          password: formData.password,
        },
        request: "login-Student",
      }
      const response = await postAws("https://api-dev.aurait.ai/auth/student-auth", "POST", loginData)

      if (response.statusCode === 200) {
        // Store login data first to ensure user can proceed even if FCM fails
        localStorage.setItem("token", response.token)
        localStorage.setItem("studentId", response.studentId)
        
        // Store student data for FCM initialization
        localStorage.setItem("studentData", JSON.stringify({
          _id: response.studentId,
          email: formData.email
        }))

        // After successful login, get the FCM token and save it
        try {
          const fcmToken = await getFCMToken()
          if (fcmToken) {
            // Store FCM token in localStorage for later use (needed for logout)
            localStorage.setItem("fcmToken", fcmToken)

            // Save FCM token to server using the updated function that handles multiple devices
            saveTokenToServer(response.studentId, fcmToken)
              .then((saveResult) => {
                console.log("FCM token saved to server:", saveResult)
                // If saving failed, schedule a retry
                if (!saveResult) {
                  console.log("Scheduling token save retry")
                  setTimeout(() => {
                    saveTokenToServer(response.studentId, fcmToken)
                      .then((retryResult) => console.log("FCM token retry result:", retryResult))
                      .catch((err) => console.error("FCM token retry error:", err))
                  }, 5000)
                }
              })
              .catch((err) => console.error("Error saving FCM token:", err))
          } else {
            console.warn("Failed to get FCM token during login")
          }
        } catch (fcmError) {
          console.error("Error with FCM during login:", fcmError)
          // Continue with login even if FCM fails
        }

        setIsLoading(false)
        navigate("/dashboard")
      } else {
        throw new Error(response.body?.error || "Login failed")
      }
    } catch (error) {
      setIsLoading(false)
      setError(error.message)
    }
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError("")
    setSuccess("")

    try {
      const forgotPasswordData = {
        body: { studentEmail: forgotPasswordEmail },
        request: "forgot-password-Student",
      }
      const response = await postAws(`https://api-dev.aurait.ai/forgotpassword/student`, "POST", forgotPasswordData)

      if (response.statusCode === 200) {
        setShowOtpModal(true)
        setTimeLeft(300)
        setIsTimedOut(false)
      } else if (response.statusCode === 400) {
        setError(response.body.error)
      } else {
        throw new Error("Unexpected error occurred")
      }
    } catch (err) {
      setError("Failed to send OTP. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleOtpVerification = async (e) => {
    e.preventDefault()
    setIsVerifying(true)
    setOtpError("")
    setPasswordError("")

    if (newPassword !== confirmNewPassword) {
      setPasswordError("Passwords do not match")
      setIsVerifying(false)
      return
    }

    try {
      const resetPasswordData = {
        body: {
          teacherEmail: forgotPasswordEmail,
          otp: otp,
          password: newPassword,
        },
        request: "verify-otp-Student",
      }
      const response = await postAws(`https://api-dev.aurait.ai/forgotpassword/student`, "POST", resetPasswordData)

      if (response.statusCode === 401) {
        setOtpError(response.body.error || "Invalid OTP, please try again")
      } else if (response.statusCode === 201) {
        setShowOtpModal(false)
        setShowSuccessModal(true)
        setTimeout(() => {
          setShowSuccessModal(false)
          navigate("/login")
        }, 10000)
      } else {
        throw new Error("Unexpected response from server")
      }
    } catch (error) {
      setOtpError("An error occurred. Please try again.")
    } finally {
      setIsVerifying(false)
    }
  }

  const handleResendOtp = async () => {
    setIsResending(true)
    await handleForgotPassword({ preventDefault: () => {} })
    setIsResending(false)
    setTimeLeft(300)
    setIsTimedOut(false)
    setOtpError("")
  }

  useEffect(() => {
    let timer
    if (showOtpModal && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timer)
            setIsTimedOut(true)
            return 0
          }
          return prevTime - 1
        })
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [showOtpModal, timeLeft])

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`
  }

  return (
    <div className="flex flex-col items-center w-full min-h-screen justify-between">
      <header className="bg-white shadow-md rounded-md top-0 z-50 w-full py-2 opacity-95 flex justify-center">
        <div className="w-full px-4 md:w-3/4 lg:w-4/5 flex items-center justify-between">
          <div className="logo-name">
            <Link to={"/"} className="flex items-center gap-2 py-2">
              <div>
                <h1 className="flex text-2xl md:text-3xl font-semibold">
                  <span>पढ़.</span>
                  <span className="gradient-text font-semibold">AI</span>
                </h1>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-4 items-center">
            <Link
              to="/signup"
              className="border-gray-600 border-2 rounded-xl bg-white text-gray-800 font-semibold text-lg py-1 px-6 rounded"
            >
              Sign up
            </Link>
            <Link to="/login" className="gradient-border">
              <button className="bg-white text-gray-800 font-semibold text-lg py-1 px-6 rounded focus:outline-none focus:shadow-outline rounded-xl">
                Login
              </button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden w-full bg-white shadow-md py-2 px-4 flex flex-col gap-3">
          <Link
            to="/signup"
            className="border-gray-600 border-2 rounded-xl bg-white text-gray-800 font-semibold text-base py-1 px-4 text-center"
          >
            Sign up
          </Link>
          <Link to="/login" className="gradient-border w-full">
            <button className="w-full bg-white text-gray-800 font-semibold text-base py-1 px-4 rounded focus:outline-none focus:shadow-outline rounded-xl">
              Login
            </button>
          </Link>
        </div>
      )}

      <div className="flex flex-col items-center justify-center p-4 flex-grow w-full">
        <div className="w-full max-w-4xl">
          <div className={`w-full mb-4 flex ${isStudent ? "justify-start" : "justify-end"}`}>
            <button className="bg-indigo-600 text-white px-4 sm:px-6 py-2 rounded-md shadow-md hover:bg-indigo-700 transition-colors duration-200 flex items-center gap-2 text-sm sm:text-base">
              {isStudent ? (
                <a className="flex items-center" href="https://padhai.aurait.ai/login">
                  <ArrowLeft className="w-4 h-4" />
                  <span className="ml-1">Login as Teacher</span>
                </a>
              ) : (
                <>
                  <span>Login as Student</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
          <div
            className={`w-full bg-white rounded-lg shadow-xl overflow-hidden ${isStudent ? "student-mode" : "teacher-mode"}`}
          >
            <div className="flex flex-col md:flex-row">
              <div
                className={`w-full md:w-1/2 p-6 sm:p-8 ${isStudent ? "order-last md:order-last" : "order-last md:order-first"}`}
              >
                <div className="mb-6">
                  <h2 className="text-2xl sm:text-3xl font-semibold mb-1">Login</h2>
                  <p className="text-base sm:text-lg font-light mb-1">
                    {isStudent ? "Welcome Back Learner!" : "Welcome Back Educator!"}
                  </p>
                  <div className="flex gap-1 text-sm">
                    <span className="text-gray-600">New?</span>
                    <Link to="/signup" className="text-indigo-600 hover:text-indigo-800">
                      Sign Up
                    </Link>
                    <span className="text-gray-600">instead.</span>
                  </div>
                </div>
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  {error && (
                    <div className="bg-red-500 text-white p-3 rounded-md flex justify-between items-center">
                      <span>{error}</span>
                      <button onClick={() => setError("")} className="text-white font-bold">
                        ✕
                      </button>
                    </div>
                  )}

                  {success && <div className="text-green-600 text-sm">{success}</div>}
                  <button
                    type="submit"
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    {isLoading ? <Loader className="animate-spin h-5 w-5 mr-2" /> : "Login"}
                  </button>
                  <div className="mt-2 text-center">
                    <button
                      type="button"
                      onClick={() => setShowForgotPasswordModal(true)}
                      className="text-indigo-600 hover:text-indigo-800 text-sm"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </form>
              </div>
              <div
                className={`w-full md:w-1/2 ${isStudent ? "order-first md:order-first" : "order-first md:order-last"}`}
              >
                <div className="h-64 md:h-full">
                  <img
                    src={isStudent ? studentImage : teacherImage}
                    alt={isStudent ? "Student" : "Teacher"}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer className="bg-gray-800 text-white w-full py-4 mt-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex gap-2 items-center">
              <span className="text-2xl font-bold">Padhai </span>
              <span className="text-sm md:text-lg font-medium">An AURA-AI Initiative</span>
            </div>

            <div className="text-center text-gray-400 text-sm order-3 md:order-2 mt-4 md:mt-0">
              © 2025 Padhai. All rights reserved.
            </div>

            <nav className="order-2 md:order-3">
              <ul className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm">
                <li>
                  <a href="#" className="hover:text-indigo-400">
                    About
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-indigo-400">
                    Contact
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-indigo-400">
                    Privacy
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-indigo-400">
                    Terms
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {showForgotPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 sm:p-8 rounded-lg shadow-xl relative max-w-md w-full">
            <button
              onClick={() => setShowForgotPasswordModal(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Forgot Password</h2>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label htmlFor="forgotPasswordEmail" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  id="forgotPasswordEmail"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              {error && <div className="text-red-600 text-sm">{error}</div>}
              {success && <div className="text-green-600 text-sm">{success}</div>}
              <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="animate-spin h-5 w-5 mr-2" />
                    Sending...
                  </>
                ) : (
                  "Request OTP"
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {showOtpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 sm:p-8 rounded-lg shadow-xl relative max-w-md w-full">
            <button
              onClick={() => setShowOtpModal(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Reset Password</h2>
            <p className="mb-4 text-sm sm:text-base">Enter the OTP sent to your email: {forgotPasswordEmail}</p>
            {!isTimedOut && <p className="mb-4 text-sm sm:text-base">Valid for 5 minutes</p>}
            <form onSubmit={handleOtpVerification} className="space-y-4">
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                  OTP
                </label>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter OTP"
                  required
                  disabled={isTimedOut}
                />
              </div>
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="block w-full pr-10 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type={showConfirmNewPassword ? "text" : "password"}
                    id="confirmNewPassword"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="block w-full pr-10 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                  >
                    {showConfirmNewPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
              {otpError && <p className="mt-1 text-sm text-red-600">{otpError}</p>}
              {passwordError && <p className="mt-1 text-sm text-red-600">{passwordError}</p>}
              {isTimedOut ? (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center justify-center"
                  disabled={isResending}
                >
                  {isResending ? (
                    <>
                      <Loader className="animate-spin h-5 w-5 mr-2" />
                      Resending...
                    </>
                  ) : (
                    "Resend OTP"
                  )}
                </button>
              ) : (
                <button
                  type="submit"
                  className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center justify-center"
                  disabled={isVerifying}
                >
                  {isVerifying ? (
                    <>
                      <Loader className="animate-spin h-5 w-5 mr-2" />
                      Verifying...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </button>
              )}
            </form>
            <div className="mt-4 flex items-center justify-end">
              <Clock className="h-5 w-5 text-gray-500 mr-2" />
              <span className={`text-gray-500 ${isTimedOut ? "text-red-500" : ""}`}>
                {isTimedOut ? "OTP Timed Out" : formatTime(timeLeft)}
              </span>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 sm:p-8 rounded-lg shadow-xl text-center max-w-md w-full">
            <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Password Reset Successful</h2>
            <p className="mb-4 text-sm sm:text-base">Your password has been updated successfully.</p>
            <p className="text-gray-500 text-sm">Redirecting to login page in 10 seconds...</p>
          </div>
        </div>
      )}
    </div>
  )
}